import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Global_Product_Family_Enum, Product_Family_Name_Enum, Product_Sub_Family_Name_Enum, User_Role_Enum } from '@kheops/graphql';
import { useGetCompanyUsersQuery } from '../queries/__generated__/getCompanyUsers.generated';
import { CompanyServices, UserAvatars } from '../common/models/user.models';
import { CompanyInfo } from './companyInfo/CompanyInfoCard';
import UserCard from './UserCard';

interface CompanyUserProps {
  company: Omit<CompanyInfo, keyof CompanyServices>;
  withDivider?: boolean;
}

interface UserProductFamilySettings {
  generalSetting?: Global_Product_Family_Enum | null;
  productFamilies: Product_Family_Name_Enum[];
  productSubFamilies: Product_Sub_Family_Name_Enum[]
}

export interface UserDisplay {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  avatars: UserAvatars;
  productFamiliesSettings: UserProductFamilySettings;
  role_name?: User_Role_Enum | null;
}

export default function CompanyUsers({ company, withDivider }: CompanyUserProps): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const { data: usersData } = useGetCompanyUsersQuery({
    variables: {
      companyId: company.id,
    },
  });
  const [usersToDisplay, setUserToDisplay] = useState<UserDisplay[]>([]);

  useEffect(() => {
    if (usersData?.role_assignment) {
      const users: UserDisplay[] = usersData.role_assignment
        .filter(({ role_name }) => role_name !== User_Role_Enum.CompanyAdmin)
        .map(({ role_name, product_families: userProductFamilies, user }) => {
          const userGlobalProductFamiliesSetting = userProductFamilies[0]?.global_setting;
          const userProductFamiliesSettings: UserProductFamilySettings = {
            generalSetting: userGlobalProductFamiliesSetting,
            productFamilies: !userGlobalProductFamiliesSetting
              ? userProductFamilies.map((userProductFamily) => userProductFamily.product_family).filter((family) => !!family) as Product_Family_Name_Enum[]
              : [],
            productSubFamilies: !userGlobalProductFamiliesSetting
              ? userProductFamilies.map((userProductFamily) => userProductFamily.product_sub_family).filter((subfamily) => !!subfamily) as Product_Sub_Family_Name_Enum[]
              : [],
          };

          return {
            id: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            avatars: {
              default_avatar: user.default_avatar,
              auth_provider_avatar_url: user.auth_provider_avatar_url,
            },
            productFamiliesSettings: userProductFamiliesSettings,
            role_name,
          };
        });

      const usersToBeDisplayed = users.filter((user) => user.productFamiliesSettings.generalSetting !== Global_Product_Family_Enum.None);

      setUserToDisplay(usersToBeDisplayed);
    }
  }, [usersData]);

  const usersComponent = useMemo(() => {
    return usersToDisplay
      .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
      .map((user) => (
        <UserCard
          key={user.id}
          user={user}
        />
      ));
  }, [usersToDisplay]);

  if (usersData && !usersToDisplay.length) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <>
      {withDivider && (
        <Divider />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        <Typography variant="titleMedium">
          {t('common:contacts')}
        </Typography>
        {usersComponent}
      </Box>
    </>
  );
}
